<template>
  <div class="team-review-timeline rounded-lg">
    <!-- Main Content -->
    <div class="flex flex-col lg:flex-row h-full">
      <!-- Left Column: Timeline -->
      <div class="w-full lg:w-1/3 p-4 overflow-y-auto">
        <div v-if="filteredTimelineItems.length === 0" class="text-center py-10">
          <v-icon size="48" color="grey lighten-1" class="mb-4">mdi-timeline-clock-outline</v-icon>
          <p class="text-gray-500">No timeline data available</p>
        </div>

        <div v-else class="custom-timeline">
          <!-- Custom Timeline -->
          <div
            class="relative pl-8 pb-4"
            v-for="(item, index) in filteredTimelineItems"
            :key="item.id"
          >
            <!-- Vertical line -->
            <div
              v-if="index !== filteredTimelineItems.length - 1"
              class="absolute left-20 top-6 h-full w-0.5 bg-gray-300"
            ></div>

            <!-- Timeline dot with time beside icon -->
            <div class="absolute left-0 top-4 flex items-center">
              <div class="text-sm text-gray-600 font-semibold whitespace-nowrap mr-1">
                {{ formatTime(item.timestamp) }}
              </div>
              <div
                class="w-8 h-8 rounded-full border-4 border-white flex items-center justify-center z-10"
                :class="getEventDotClass(item.mainActivity)"
              >
                <v-icon small :color="getEventIconColor(item.mainActivity)">{{
                  getEventIcon(item.mainActivity)
                }}</v-icon>
              </div>
            </div>

            <!-- Timeline card -->
            <div
              class="ml-20 mb-4 cursor-pointer hover:bg-gray-100 p-2 rounded bg-white"
              :class="selectedItemIndex === index ? 'bg-blue-50' : ''"
              @click="selectTimelineItem(index)"
            >
              <div class="mt-2 flex justify-between items-center">
                <span class="font-medium text-sm text-gray-800" v-if="item.mainActivity === 'HALT'"
                  >{{ item.mainActivity }}
                </span>
                <span class="font-medium text-sm text-gray-800" v-else>{{
                  item.action.toUpperCase()
                }}</span>
                <div v-if="item.batteryHealth" class="flex items-center justify-end">
                  <BatteryIcons
                    :batteryLevel="item.batteryHealth"
                    :batteryColor="'green'"
                    :rotate="true"
                  />
                  <span class="ml-1 text-sm text-green-600"> {{ item.batteryHealth }}% </span>
                </div>
              </div>
              <div class="text-sm text-gray-600 truncate">{{ item.location }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Right Column: Map and Details Panel -->
      <div class="w-full lg:w-2/3 p-4 border-l border-gray-200 flex flex-col">
        <div class="bg-white rounded-lg shadow-sm h-full flex flex-col">
          <!-- Map Controls -->
          <!-- <div class="p-3 border-b border-gray-200 flex justify-between items-center">
            <h3 class="font-semibold text-gray-800">Activity Map</h3>
            <div class="flex space-x-1">
              <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                  <v-btn icon @click="toggleSatelliteView" v-on="on" small>
                    <v-icon :color="isSatelliteView ? 'primary' : ''">mdi-satellite</v-icon>
                  </v-btn>
                </template>
                <span>Satellite View</span>
              </v-tooltip>

              <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                  <v-btn icon @click="toggleRouteVisibility" v-on="on" small>
                    <v-icon :color="showRoute ? 'primary' : ''">mdi-vector-polyline</v-icon>
                  </v-btn>
                </template>
                <span>{{ showRoute ? 'Hide Route' : 'Show Route' }}</span>
              </v-tooltip>

              <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                  <v-btn icon @click="fitMapToMarkers" v-on="on" small>
                    <v-icon>mdi-fit-to-screen</v-icon>
                  </v-btn>
                </template>
                <span>Fit to Markers</span>
              </v-tooltip>
            </div>
          </div> -->

          <!-- Google Map Container -->
          <div class="flex-grow relative" style="height: 60%">
            <div id="team-review-timeline-map" class="w-full h-full min-h-[400px]"></div>

            <!-- Map Loading Overlay -->
            <div
              v-if="mapLoading"
              class="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center"
            >
              <v-progress-circular
                indeterminate
                size="32"
                :color="$vuetify.theme.currentTheme.primary"
              ></v-progress-circular>
            </div>
          </div>

          <!-- Details Panel (only shown when item is selected) -->
          <div
            v-if="selectedTimelineItem"
            class="border-t border-gray-200 overflow-y-auto"
            style="height: 35%"
          >
            <div class="p-4">
              <div class="flex justify-between items-start mb-2">
                <div>
                  <h3 class="text-lg font-bold text-gray-800">
                    {{ selectedTimelineItem.mainActivity }}
                  </h3>
                  <div class="flex items-center mt-1">
                    <v-chip
                      :color="getEventColor(selectedTimelineItem.mainActivity)"
                      small
                      text-color="white"
                    >
                      {{ selectedTimelineItem.mainActivity }}
                    </v-chip>
                    <span class="text-sm text-gray-500 ml-2">
                      {{ formatDate(selectedTimelineItem.timestamp) }} at
                      {{ formatTime(selectedTimelineItem.timestamp) }}
                    </span>
                  </div>
                </div>
                <v-btn icon @click="clearSelection" small>
                  <v-icon>mdi-close</v-icon>
                </v-btn>
              </div>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                <div class="bg-gray-50 p-2 rounded">
                  <div class="text-xs text-gray-500">Location</div>
                  <div class="text-sm truncate">{{ selectedTimelineItem.location }}</div>
                </div>

                <div class="bg-gray-50 p-2 rounded">
                  <div class="text-xs text-gray-500">Distance</div>
                  <div class="text-sm">{{ selectedTimelineItem.distance }} km</div>
                </div>

                <div v-if="selectedTimelineItem.batteryHealth" class="bg-gray-50 p-2 rounded">
                  <div class="text-xs text-gray-500">Battery</div>
                  <div class="text-sm">{{ selectedTimelineItem.batteryHealth }}%</div>
                </div>
              </div>

              <!-- Sub Activities - removed as per new data structure -->

              <!-- Todos -->
              <div
                v-if="selectedTimelineItem.todos && selectedTimelineItem.todos.length > 0"
                class="mb-3"
              >
                <h4 class="font-semibold text-gray-700 text-sm mb-1">Todos</h4>
                <!-- Grouped Todos by taskId -->
                <div
                  v-for="(taskGroup, taskId) in groupedTodos"
                  :key="taskId"
                  class="border border-gray-200 rounded p-3 mb-2"
                >
                  <div class="flex justify-between items-center mb-2">
                    <span class="font-medium text-gray-800">{{
                      taskGroup.taskInfo?.taskTitle
                    }}</span>
                    <v-chip
                      :color="taskGroup.taskInfo?.taskStatus === 'COMPLETED' ? 'green' : 'orange'"
                      small
                      text-color="white"
                    >
                      {{ taskGroup.taskInfo?.taskStatus }}
                    </v-chip>
                  </div>

                  <div class="space-y-2">
                    <div
                      v-for="(todo, idx) in taskGroup.activities"
                      :key="idx"
                      class="flex flex-col text-sm bg-gray-50 p-2 rounded"
                    >
                      <div class="flex justify-between items-center">
                        <span class="text-gray-700">{{ titleCase(todo.activity?.action) }}</span>
                        <span class="text-gray-500 text-xs">
                          {{ $dayjs(todo.activity?.time).format('DD/MM/YYYY hh:mm A') }}
                        </span>
                      </div>
                      <div class="text-gray-600 text-xs mt-1 truncate">
                        {{ todo.activity?.location?.formatted_address || 'Location not available' }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Empty State (when no item is selected) -->
          <div v-else class="border-t border-gray-200 p-8 text-center" style="height: 35%">
            <v-icon size="48" color="grey lighten-1" class="mb-3">mdi-map-marker</v-icon>
            <h3 class="text-lg font-medium text-gray-700 mb-1">Select an activity</h3>
            <p class="text-gray-500 text-sm">
              Click on any timeline item to view its location on the map
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Attachment Dialog -->
    <v-dialog v-model="showAttachmentDialog" max-width="600px">
      <v-card>
        <v-card-title class="text-gray-800 pb-2">
          {{ currentAttachment?.name }}
          <v-spacer></v-spacer>
          <v-btn icon @click="showAttachmentDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <div class="flex justify-center">
            <img
              v-if="currentAttachment && isImage(currentAttachment.url)"
              :src="currentAttachment.url"
              :alt="currentAttachment.name"
              class="max-w-full max-h-[70vh]"
            />
            <div v-else class="text-center py-10">
              <v-icon size="64" class="mb-4">mdi-file-document-outline</v-icon>
              <p>Cannot preview this file type</p>
              <v-btn color="primary" :href="currentAttachment?.url" target="_blank" class="mt-4">
                <v-icon left>mdi-download</v-icon>
                Download File
              </v-btn>
            </div>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
/* global google */
import BatteryIcons from '@/components/Workforce/AttendanceManagement/BatteryIcons.vue'
// Import the mock location data directly
import mockLocationData from '@/utils/mockLocation.json'
import { convertToTitleCase } from '@/utils/common'

export default {
  name: 'TeamReviewTimeline',
  components: {
    BatteryIcons
  },
  props: {
    taskSteps: {
      type: Array,
      default: () => []
    },
    orgConfig: {
      type: Object,
      default: () => null
    },
    employees: {
      type: Array,
      default: () => []
    },
    teamData: {
      type: Array,
      default: () => []
    },
    view: {
      type: Boolean,
      default: true
    },
    search: {
      type: String,
      default: ''
    },
    selectedTaskStatus: {
      type: Array,
      default: () => []
    },
    selectedColumns: {
      type: Array,
      default: () => []
    },
    customDates: {
      type: Array,
      default: () => []
    },
    startOfMonth: {
      type: String,
      default: ''
    },
    endOfMonth: {
      type: String,
      default: ''
    },
    userIds: {
      type: Array,
      default: () => []
    },
    dateRangeType: {
      type: String,
      default: 'Monthly'
    },
    apiLoading: {
      type: Boolean,
      default: false
    },
    locationHistory: {
      type: Array,
      default: () => []
    },
    selectedUserId: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      // Timeline data
      timelineItems: [],
      filteredTimelineItems: [],
      selectedItemIndex: -1,
      selectedTimelineItem: null,

      // Filters
      selectedUser: null,
      dateRange: [],
      dateMenu: false,
      users: [],

      // Attachment dialog
      showAttachmentDialog: false,
      currentAttachment: null,

      // Map data
      map: null,
      markers: [],
      polylines: [],
      routePolyline: null,
      mapLoading: false,
      mapLoaded: false,
      showRoute: true,
      directionsService: null,
      directionsRenderer: null,

      // Add imported mock data to data properties
      mockLocationData: mockLocationData
    }
  },
  computed: {
    dateRangeText() {
      return this.dateRange.length === 2
        ? `${this.formatDateDisplay(this.dateRange[0])} to ${this.formatDateDisplay(
            this.dateRange[1]
          )}`
        : 'Select date range'
    },
    groupedTodos() {
      if (!this.selectedTimelineItem || !this.selectedTimelineItem.todos) {
        return {}
      }

      // Group todos by taskId._id
      const grouped = {}
      this.selectedTimelineItem.todos.forEach((todo) => {
        const taskId = todo.taskId?._id || 'unknown'
        if (!grouped[taskId]) {
          grouped[taskId] = {
            taskInfo: todo.taskId,
            activities: []
          }
        }
        grouped[taskId].activities.push(todo)
      })

      return grouped
    }
  },
  watch: {
    employees: {
      handler(newVal) {
        if (newVal && newVal.length > 0) {
          this.users = newVal.map((emp) => ({
            _id: emp._id,
            name: `${emp.firstName} ${emp.lastName}`
          }))
        }
      },
      immediate: true
    },
    teamData: {
      handler() {
        this.initializeTimelineData()
      },
      immediate: true
    },
    filteredTimelineItems: {
      handler() {
        if (this.mapLoaded) {
          this.renderMapElements()
        }
      },
      immediate: true
    },
    selectedTimelineItem: {
      handler(newItem) {
        if (newItem && this.mapLoaded) {
          this.highlightSelectedMarker()
        }
      }
    },
    showRoute: {
      handler() {
        if (this.routePolyline) {
          this.routePolyline.setVisible(this.showRoute)
        }
        if (this.directionsRenderer) {
          this.directionsRenderer.setMap(this.showRoute ? this.map : null)
        }
      }
    },
    locationHistory: {
      handler(newVal) {
        if (newVal) {
          // Check if we have actual location data
          let hasLocationData = false

          if (Array.isArray(newVal) && newVal.length > 0) {
            // If the first item has locationData property
            if (newVal[0].locationData) {
              hasLocationData = newVal[0].locationData.length > 0
            } else {
              // Otherwise check the array itself
              hasLocationData = newVal.length > 0
            }
          } else if (newVal && newVal.locationData) {
            // If it's an object with locationData property
            hasLocationData = newVal.locationData.length > 0
          }

          if (hasLocationData) {
            this.processLocationHistory(newVal)
          }
        }
        // this.processLocationHistory(this.mockLocationData)
      },
      immediate: true
    }
  },
  mounted() {
    this.initializeTimelineData()
    // Set default date range to last 7 days
    const today = new Date()
    const lastWeek = new Date()
    lastWeek.setDate(today.getDate() - 7)
    this.dateRange = [lastWeek.toISOString().split('T')[0], today.toISOString().split('T')[0]]

    // Initialize map
    this.initializeMap()
  },
  beforeDestroy() {
    // Clean up map resources
    if (this.map) {
      this.map = null
    }
  },
  methods: {
    titleCase(str) {
      return convertToTitleCase(str)
    },
    processLocationHistory(locationHistory) {
      // Handle the case where locationHistory is an array with one object containing locationData
      let locationData = []

      if (Array.isArray(locationHistory) && locationHistory.length > 0) {
        // If the first item has locationData property, use that
        if (locationHistory[0].locationData) {
          locationData = locationHistory[0].locationData
        } else {
          // Otherwise assume the array itself contains the location data
          locationData = locationHistory
        }
      } else if (locationHistory && locationHistory.locationData) {
        // If locationHistory is an object with locationData property
        locationData = locationHistory.locationData
      }

      // Transform location data into timeline items
      const timelineItems = locationData.map((locationItem, index) => {
        // Extract location details
        const location = locationItem.location || {}
        const address = location.formatted_address || 'Unknown Location'
        const lat = location.geometry ? location.geometry.lat : 0
        const lng = location.geometry ? location.geometry.lng : 0
        const batteryHealth = locationItem?.deviceData?.batteryHealth || ''

        // Extract activity details
        const activity = locationItem.activity || {}
        const mainActivity = activity.mainActivity || 'UNKNOWN'
        // subActivities removed as per new data structure
        const todos = activity.todos || []
        const timestamp = activity.time ? new Date(activity.time) : new Date()

        // Create timeline item
        return {
          id: locationItem._id || index,
          mainActivity: mainActivity,
          action: activity.action,
          todos: todos,
          timestamp: timestamp,
          location: address,
          lat: lat,
          lng: lng,
          distance: 0, // Will be set from pairDistance data
          batteryHealth: batteryHealth
        }
      })

      // Set distances from pairDistance data
      if (locationHistory.length > 0 && locationHistory[0].dailyDistanceTraveled?.pairDistance) {
        if (Array.isArray(locationHistory[0].dailyDistanceTraveled?.pairDistance)) {
          const pairDistances = locationHistory[0].dailyDistanceTraveled?.pairDistance

          pairDistances.forEach((pair, index) => {
            // Find the pair_X field (pair_1, pair_2, etc.)
            const pairKey = Object.keys(pair).find((key) => key.startsWith('pair_'))
            // if (
            //   timelineItems[index + 1].type === 'TaskStart' ||
            //   timelineItems[index + 1].type === 'TaskEnded'
            // ) {

            if (pairKey && timelineItems[index + 1]) {
              timelineItems[index + 1].distance = pair[pairKey]
            }
          })
        }
      }

      this.timelineItems = timelineItems
      this.filteredTimelineItems = [...timelineItems]

      // Render map elements with the new data
      if (this.mapLoaded) {
        this.$nextTick(() => {
          this.renderMapElements()
        })
      }
    },

    initializeTimelineData() {
      // Process mock data directly
      // this.processLocationHistory(this.mockLocationData)
    },

    initializeMap() {
      this.mapLoading = true

      // Check if Google Maps is already loaded
      if (window.google && window.google.maps) {
        this.initMap()
        return
      }

      // Load Google Maps API
      const script = document.createElement('script')
      script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.VUE_APP_MAP_KEY}&libraries=places`
      script.async = true
      script.defer = true
      script.onload = () => {
        this.initMap()
      }
      script.onerror = () => {
        console.error('Error loading Google Maps API')
        this.mapLoading = false
        this.$toast.error('Failed to load map')
      }
      document.head.appendChild(script)
    },

    initMap() {
      const mapElement = document.getElementById('team-review-timeline-map')
      if (!mapElement) {
        console.error('Map element not found')
        this.mapLoading = false
        return
      }

      const mapOptions = {
        center: { lat: 28.6139, lng: 77.209 }, // Default to New Delhi
        zoom: 12,
        mapTypeId: google.maps.MapTypeId.ROADMAP
      }

      this.map = new google.maps.Map(mapElement, mapOptions)
      this.mapLoaded = true
      this.mapLoading = false

      // Initialize Directions Service and Renderer
      this.directionsService = new google.maps.DirectionsService()
      this.directionsRenderer = new google.maps.DirectionsRenderer({
        map: this.map,
        suppressMarkers: true, // Use our custom markers instead
        polylineOptions: {
          strokeColor: '#4285F4',
          strokeOpacity: 0.8,
          strokeWeight: 4
        }
      })

      // Render markers and route after map is initialized
      this.$nextTick(() => {
        this.renderMapElements()
      })

      // Add map click listener to clear selection
      this.map.addListener('click', () => {
        this.clearSelection()
      })
    },

    renderMapElements() {
      if (!this.map || !this.directionsService || !this.directionsRenderer) return

      // Clear existing markers and polylines
      this.clearMapElements()

      if (this.filteredTimelineItems.length === 0) return

      const bounds = new google.maps.LatLngBounds()

      // Create markers for each timeline item
      this.filteredTimelineItems.forEach((item, index) => {
        if (!item.lat || !item.lng) return

        const position = { lat: item.lat, lng: item.lng }
        bounds.extend(position)

        // Create marker with custom icon
        const marker = new google.maps.Marker({
          position,
          map: this.map,
          title: `${index + 1}. ${item.mainActivity}`,
          icon: {
            path: google.maps.SymbolPath.CIRCLE,
            scale: 10,
            fillColor: this.getEventColorHex(item.mainActivity),
            fillOpacity: 1,
            strokeColor: 'white',
            strokeWeight: 2
          },
          label: {
            text: String(index + 1),
            color: 'white',
            fontSize: '12px',
            fontWeight: 'bold'
          }
        })

        // Store reference to marker
        marker.timelineIndex = index

        // Add click event to marker
        marker.addListener('click', () => {
          this.selectTimelineItem(index)
        })

        this.markers.push(marker)
      })

      // Calculate and display actual route between consecutive points
      if (this.markers.length > 1) {
        this.calculateRoute()
      }

      // Fit map to bounds
      if (this.markers.length > 0) {
        this.map.fitBounds(bounds)

        // If only one marker, zoom out a bit
        if (this.markers.length === 1) {
          google.maps.event.addListenerOnce(this.map, 'bounds_changed', () => {
            this.map.setZoom(15)
          })
        }
      }
    },

    calculateRoute() {
      // Clear previous route
      this.directionsRenderer.set('directions', null)

      // Create waypoints from markers (excluding first and last)
      const waypoints = []
      for (let i = 1; i < this.markers.length - 1; i++) {
        waypoints.push({
          location: this.markers[i].getPosition(),
          stopover: false
        })
      }

      // Request directions
      const request = {
        origin: this.markers[0].getPosition(),
        destination: this.markers[this.markers.length - 1].getPosition(),
        waypoints: waypoints,
        travelMode: google.maps.TravelMode.DRIVING
      }

      this.directionsService.route(request, (result, status) => {
        if (status === google.maps.DirectionsStatus.OK) {
          this.directionsRenderer.setDirections(result)
        } else {
          console.error('Directions request failed due to ' + status)
          // Fallback to straight line if directions fail
          this.createStraightLineRoute()
        }
      })
    },

    createStraightLineRoute() {
      // Fallback method to create a straight line route if Directions API fails
      if (this.markers.length > 1) {
        const path = this.markers.map((marker) => marker.getPosition())

        this.routePolyline = new google.maps.Polyline({
          path: path,
          geodesic: true,
          strokeColor: '#4285F4',
          strokeOpacity: 0.8,
          strokeWeight: 4,
          visible: this.showRoute
        })

        this.routePolyline.setMap(this.map)
        this.polylines.push(this.routePolyline)
      }
    },

    clearMapElements() {
      // Clear markers
      this.markers.forEach((marker) => {
        marker.setMap(null)
      })
      this.markers = []

      // Clear polylines
      this.polylines.forEach((polyline) => {
        polyline.setMap(null)
      })
      this.polylines = []

      // Clear route polyline
      if (this.routePolyline) {
        this.routePolyline.setMap(null)
        this.routePolyline = null
      }

      // Clear directions
      if (this.directionsRenderer) {
        this.directionsRenderer.set('directions', null)
      }
    },

    highlightSelectedMarker() {
      if (!this.map || !this.selectedTimelineItem) return

      // Reset all markers
      this.markers.forEach((marker) => {
        const item = this.filteredTimelineItems[marker.timelineIndex]
        if (item) {
          marker.setIcon({
            path: google.maps.SymbolPath.CIRCLE,
            scale: 10,
            fillColor: this.getEventColorHex(item.mainActivity),
            fillOpacity: 1,
            strokeColor: 'white',
            strokeWeight: 2
          })
        }
      })

      // Highlight selected marker
      const selectedIndex = this.filteredTimelineItems.findIndex(
        (item) => item.id === this.selectedTimelineItem.id
      )

      if (selectedIndex !== -1) {
        const selectedMarker = this.markers[selectedIndex]
        if (selectedMarker) {
          selectedMarker.setIcon({
            path: google.maps.SymbolPath.CIRCLE,
            scale: 14,
            fillColor: '#FFD700', // Gold color for highlight
            fillOpacity: 1,
            strokeColor: '#000000',
            strokeWeight: 2
          })

          // Pan to selected marker
          this.map.panTo(selectedMarker.getPosition())
        }
      }
    },

    selectTimelineItem(index) {
      this.selectedItemIndex = index
      this.selectedTimelineItem = this.filteredTimelineItems[index]

      // Highlight marker on map
      this.highlightSelectedMarker()
    },

    clearSelection() {
      this.selectedItemIndex = -1
      this.selectedTimelineItem = null
    },

    applyFilters() {
      // Apply filters to timeline items
      this.filteredTimelineItems = this.timelineItems.filter((item) => {
        // User filter
        if (this.selectedUser && item.userId !== this.selectedUser) return false

        // Date range filter
        if (this.dateRange.length === 2) {
          const itemDate = new Date(item.timestamp)
          const startDate = new Date(this.dateRange[0])
          const endDate = new Date(this.dateRange[1])
          if (itemDate < startDate || itemDate > endDate) return false
        }

        return true
      })
    },

    clearFilters() {
      this.selectedUser = null
      this.dateRange = []
      this.filteredTimelineItems = [...this.timelineItems]
    },

    getEventColor(type) {
      const colorMap = {
        CHECK_IN_OUT: 'green',
        TRAVELLING: 'orange',
        HALT: 'blue'
      }
      return colorMap[type] || 'grey'
    },

    getEventColorHex(type) {
      const colorMap = {
        CHECK_IN_OUT: '#4CAF50',
        TRAVELLING: '#FF9800',
        HALT: '#2196F3'
      }
      return colorMap[type] || '#9E9E9E'
    },

    getEventDotClass(type) {
      const classMap = {
        CHECK_IN_OUT: 'bg-green-500',
        TRAVELLING: 'bg-orange-500',
        HALT: 'bg-blue-500'
      }
      return classMap[type] || 'bg-gray-500'
    },

    getEventIconColor(type) {
      const colorMap = {
        CHECK_IN_OUT: 'white',
        TRAVELLING: 'white',
        HALT: 'white'
      }
      return colorMap[type] || 'white'
    },

    getEventIcon(type) {
      const iconMap = {
        CHECK_IN_OUT: 'mdi-login-variant',
        TRAVELLING: 'mdi-car',
        HALT: 'mdi-pause-circle'
      }
      return iconMap[type] || 'mdi-map-marker'
    },

    formatDate(timestamp) {
      return new Date(timestamp).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      })
    },

    formatDateDisplay(dateString) {
      const date = new Date(dateString)
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
      })
    },

    formatTime(timestamp) {
      return new Date(timestamp).toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit'
      })
    },

    viewAttachment(attachment) {
      this.currentAttachment = attachment
      this.showAttachmentDialog = true
    },

    isImage(url) {
      return /\.(jpeg|jpg|gif|png|webp)$/i.test(url)
    },
    markAsComplete() {
      // In a real implementation, this would update the item status
      this.$toast.success(`${this.selectedTimelineItem.mainActivity} marked as complete`)
    },

    toggleRouteVisibility() {
      this.showRoute = !this.showRoute
    },

    fitMapToMarkers() {
      if (!this.map || this.markers.length === 0) return

      const bounds = new google.maps.LatLngBounds()
      this.markers.forEach((marker) => {
        bounds.extend(marker.getPosition())
      })

      this.map.fitBounds(bounds)
    }
  }
}
</script>

<style scoped>
.team-review-timeline {
  height: calc(100vh - 170px);
}

/* Custom scrollbar */
.overflow-y-auto::-webkit-scrollbar {
  width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

/* Line clamp for truncating text */
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* Timeline specific styles */
.custom-timeline .relative {
  min-height: 60px;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .team-review-timeline {
    height: auto;
    min-height: 500px;
  }

  .flex-col.lg\:flex-row {
    flex-direction: column;
  }

  .w-full.lg\:w-1\/2 {
    width: 100%;
  }

  .border-l.border-gray-200 {
    border-left: none;
    border-top: 1px solid #e5e7eb;
  }

  .flex-col.lg\:flex-row.h-full {
    height: auto;
  }
}
</style>
